import { TsosStateProps } from '@pages/MeasurementVisualization/utils';
import { ApiCommonResponsesHarmonicBackgroundLevelParameterValuesResponse } from '@redux/apiSlices/harmonicBackgroundLevelApi';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface HarmonicChartDeviceData {
  tsoId: number;
  measurementDeviceId: number;
  deviceLabel?: string;
  substationLabel?: string;
  measurementPointLabel?: string;
  start: string;
  end: string;
  technicalName: string;
  harmonics: ApiCommonResponsesHarmonicBackgroundLevelParameterValuesResponse[];
}

export interface HarmonicBackgroundLevelState {
  selectedTso: string;
  activeTsos: TsosStateProps[] | [];
  activeTsoName: string;
  start: string | undefined;
  end: string | undefined;
  deviceTechnicalNames: { [key: string]: string[] };
  allEvenHarmonics: any;
  allOddHarmonics: any;
  measurementType: 'LL' | 'LE' | '';
  selectedHarmonicsTechnicalNames: string[];
  selectedHarmonicsIds: number[];
  dataToBeDisplayedInChart: HarmonicChartDeviceData[];
  harmonicBackgroundLevel: {
    [key: string]: ApiCommonResponsesHarmonicBackgroundLevelParameterValuesResponse[];
  };
  isLoading: boolean;
  triedSubmitting: boolean;
  mandatoryFieldsStatus: {
    tso: boolean | undefined;
    start: boolean | undefined;
    end: boolean | undefined;
    measurementType: boolean | undefined;
    harmonics: boolean | undefined;
    selectedDevices: { [key: string]: boolean | undefined };
    technicalName: string | undefined;
  };
}

const initialState: HarmonicBackgroundLevelState = {
  selectedTso: '',
  activeTsos: [],
  activeTsoName: '',
  start: undefined,
  end: undefined,
  deviceTechnicalNames: {},
  allEvenHarmonics: false,
  allOddHarmonics: false,
  selectedHarmonicsTechnicalNames: [],
  dataToBeDisplayedInChart: [],
  harmonicBackgroundLevel: {},
  selectedHarmonicsIds: [],
  measurementType: '',
  isLoading: false,
  triedSubmitting: false,
  mandatoryFieldsStatus: {
    tso: undefined,
    start: undefined,
    end: undefined,
    measurementType: undefined,
    harmonics: undefined,
    selectedDevices: {},
    technicalName: undefined,
  },
};

const harmonicBackgroundLevelSlice = createSlice({
  name: 'harmonicBackgroundLevel',
  initialState,
  reducers: {
    setSelectedTso: (state, action: PayloadAction<string>) => {
      state.selectedTso = action.payload;
    },
    setActiveTsos: (state, action: PayloadAction<TsosStateProps[]>) => {
      state.activeTsos = action.payload;
    },
    setActiveTsoName: (state, action: PayloadAction<string>) => {
      state.activeTsoName = action.payload;
    },
    setStart: (state, action: PayloadAction<string>) => {
      state.start = action.payload;
    },
    setEnd: (state, action: PayloadAction<string>) => {
      state.end = action.payload;
    },
    setDeviceTechnicalNames: (state, action: PayloadAction<{ [key: string]: string[] }>) => {
      state.deviceTechnicalNames = action.payload;
    },
    setAllEvenHarmonics: (state, action) => {
      state.allEvenHarmonics = action.payload;
    },
    setAllOddHarmonics: (state, action) => {
      state.allOddHarmonics = action.payload;
    },
    setSelectedHarmonicsTechnicalNames: (state, action) => {
      state.selectedHarmonicsTechnicalNames = action.payload;
    },
    setSelectedHarmonicsIds: (state, action) => {
      state.selectedHarmonicsIds = action.payload;
    },
    setDataToBeDisplayedInChart: (state, action: PayloadAction<HarmonicChartDeviceData[]>) => {
      state.dataToBeDisplayedInChart = action.payload;
    },

    setHarmonicBackgroundLevel: (
      state,
      action: PayloadAction<{
        deviceKey: string; // tsoId and device like `${tsoId}-${deviceId}`
        data: ApiCommonResponsesHarmonicBackgroundLevelParameterValuesResponse[];
      }>
    ) => {
      state.harmonicBackgroundLevel[action.payload.deviceKey] = action.payload.data;
    },
    setMeasurementType: (state, action: PayloadAction<'LL' | 'LE' | ''>) => {
      state.measurementType = action.payload;
    },
    resetAll: () => initialState,
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetQuickSelect: (state) => {
      state.allEvenHarmonics = false;
      state.allOddHarmonics = false;
    },
    setTriedSubmitting: (state, action: PayloadAction<boolean>) => {
      state.triedSubmitting = action.payload;
    },
    setMandatoryFieldsStatus: (state, action: PayloadAction<HarmonicBackgroundLevelState['mandatoryFieldsStatus']>) => {
      state.mandatoryFieldsStatus = action.payload;
    },
  },
});

export const {
  setSelectedTso,
  setActiveTsos,
  setActiveTsoName,
  setDeviceTechnicalNames,
  setEnd,
  setStart,
  setAllEvenHarmonics,
  setAllOddHarmonics,
  resetAll,
  setSelectedHarmonicsTechnicalNames,
  setDataToBeDisplayedInChart,
  setHarmonicBackgroundLevel,
  setSelectedHarmonicsIds,
  setMeasurementType,
  setIsLoading,
  resetQuickSelect,
  setMandatoryFieldsStatus,
  setTriedSubmitting,
} = harmonicBackgroundLevelSlice.actions;

export const selectSelectedTso = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.selectedTso;

export const selectActiveTsos = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.activeTsos;

export const selectActiveTsoName = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.activeTsoName;

export const selectStart = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.start;

export const selectAllEvenHarmonics = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.allEvenHarmonics;

export const selectAllOddHarmonics = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.allOddHarmonics;

export const selectEnd = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.end;

export const selectSelectedHarmonicsTechnicalNames = (state: {
  harmonicBackgroundLevel: HarmonicBackgroundLevelState;
}) => state.harmonicBackgroundLevel.selectedHarmonicsTechnicalNames;

export const selectDeviceTechnicalNames = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.deviceTechnicalNames;

export const selectSelectedHarmonicsIds = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.selectedHarmonicsIds;

export const selectDataToBeDisplayedInChart = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.dataToBeDisplayedInChart;

export const selectHarmonicBackgroundLevel = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.harmonicBackgroundLevel;

export const selectMeasurementType = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.measurementType;

export const selectIsLoading = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.isLoading;

export const selectTriedSubmitting = (state: { harmonicBackgroundLevel: HarmonicBackgroundLevelState }) =>
  state.harmonicBackgroundLevel.triedSubmitting;

export const validateMandatoryFields = (state: HarmonicBackgroundLevelState) => {
  const { activeTsos, start, end, selectedHarmonicsIds, measurementType, deviceTechnicalNames } = state;

  const selectedDevicesStatus = Object.fromEntries(
    Object.entries(deviceTechnicalNames).map(([key, devices]) => [key, devices.length > 0])
  );

  const mandatoryFieldsStatus = {
    tso: activeTsos.length > 0,
    start: !!start,
    end: !!end,
    measurementType: !!measurementType,
    harmonics: (selectedHarmonicsIds?.length ?? 0) > 0,
    selectedDevices: selectedDevicesStatus,
  };

  const allValid =
    Object.values(mandatoryFieldsStatus).every(Boolean) &&
    Object.values(mandatoryFieldsStatus.selectedDevices).every(Boolean);

  return { mandatoryFieldsStatus, allValid };
};

export default harmonicBackgroundLevelSlice.reducer;
